import { useState, useEffect, useCallback } from 'react'
import { directorApi } from '@/services/api/director'
import type { HRDirectorDecisionRow } from '@/types/director'
import { PAYMENT_TYPE_LABELS, TIME_IMPACT_LABELS, DECISION_TYPE_LABELS, DECISION_TYPE_COLORS } from '@/types/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { HrPageHeader } from '@/components/hr/HrPageHeader'

export function HRDirectorDecisionsPage() {
  const [decisions, setDecisions] = useState<HRDirectorDecisionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterImpact, setFilterImpact] = useState<string>('')
  const [filterPayment, setFilterPayment] = useState<string>('')

  const fetchData = useCallback(async () => {
    try {
      const data = await directorApi.getHRDecisions()
      setDecisions(data)
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Ein Fehler ist aufgetreten' }))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const filtered = decisions.filter(d => {
    if (filterImpact && d.time_impact !== filterImpact) return false
    if (filterPayment && d.payment_type !== filterPayment) return false
    return true
  })

  const counts = {
    deducted: decisions.filter(d => d.time_impact === 'deducted').length,
    neutral: decisions.filter(d => d.time_impact === 'neutral').length,
    credited: decisions.filter(d => d.time_impact === 'credited').length,
  }

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  return (
    <div className="flex flex-col min-h-full">
      <HrPageHeader
        title="Direktor-Entscheidungen"
        description="Entscheidungen des Direktors mit Zahlungsart und Zeitauswirkung — zur Durchsicht für HR."
      />

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

        {/* hr2-v2 (E9): KPI-Karten auf leitendes Kartendesign mit Ton-Farbe */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-red-400 p-4">
            <div className="text-xs text-gray-500">Abgezogen</div>
            <div className="text-2xl font-bold text-red-700">{counts.deducted}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-amber-400 p-4">
            <div className="text-xs text-gray-500">Nicht abgezogen</div>
            <div className="text-2xl font-bold text-amber-700">{counts.neutral}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 p-4">
            <div className="text-xs text-gray-500">Gutgeschrieben</div>
            <div className="text-2xl font-bold text-green-700">{counts.credited}</div>
          </div>
        </div>

      <div className="flex gap-3 mb-4">
        <select value={filterImpact} onChange={e => setFilterImpact(e.target.value)}
          className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm">
          <option value="">Alle Zeit-Impacts</option>
          <option value="deducted">Abgezogen</option>
          <option value="neutral">Nicht abgezogen</option>
          <option value="credited">Gutgeschrieben</option>
        </select>
        <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}
          className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm">
          <option value="">Alle Zahlungsarten</option>
          <option value="private">Privat</option>
          <option value="school">Schule</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lehrer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entscheidung</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zahlung</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zeit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direktor</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dokumente</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notiz</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-4 text-center text-gray-500">Keine Entscheidungen gefunden</td></tr>
            )}
            {filtered.map(d => (
              <tr key={d.decision_id || d.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">{new Date(d.created_at).toLocaleDateString('de-CH')}</td>
                <td className="px-4 py-3 text-sm">{d.requesting_teacher_name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    DECISION_TYPE_COLORS[d.decision_type] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {DECISION_TYPE_LABELS[d.decision_type] || d.decision_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{PAYMENT_TYPE_LABELS[d.payment_type] || d.payment_type}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{TIME_IMPACT_LABELS[d.time_impact] || d.time_impact}</td>
                <td className="px-4 py-3 text-sm">{d.director_name}</td>
                <td className="px-4 py-3 text-sm">
                  {(d.document_requests?.length ?? 0) > 0 || d.custom_document_request ? (
                    <div className="space-y-0.5">
                      {d.document_requests?.map((doc, i) => (
                        <div key={i} className="text-xs text-gray-600">• {doc}</div>
                      ))}
                      {d.custom_document_request && (
                        <div className="text-xs text-gray-600 italic">• {d.custom_document_request}</div>
                      )}
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={d.rejection_note || ''}>
                  {d.rejection_note || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  )
}
